import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import {
  CheckCircle2, Circle, Flame, Plus, Trash2, Settings,
  BookOpen, Church, Heart, Moon, Sun, Cross, Star, Ban,
  UtensilsCrossed, HandHeart, Bell, CircleCheck,
  Calendar as CalendarIcon, BarChart3, Sparkles, ChevronLeft, ChevronRight, AlertTriangle,
  Trophy, Users, X, Leaf, Edit3, ArrowUp, ArrowDown, Download, BookMarked, FileText
} from "lucide-react";
import {
  getHabitsToday, logHabit, getHabitStats, getHabitTemplates,
  applyHabitTemplate, createHabit, deleteHabit, resetHabitProgress,
  getLiturgicalSuggestions, dismissLiturgicalSuggestion, addSuggestedHabits,
  getHabitReminderSettings, updateHabitReminderSettings, getMonthlyStats,
  updateHabit, reorderHabits, getJournalEntries, updateLogNotes, exportHabitsCsv
} from "@/lib/api";
import { useLiturgicalTheme } from "@/contexts/LiturgicalThemeContext";
import { toast } from "sonner";
import ChallengesTab from "@/pages/ChallengesTab";
import AccountabilityTab from "@/pages/AccountabilityTab";

const ICON_MAP = {
  "sun": Sun, "moon": Moon, "church": Church, "heart": Heart, "book": BookOpen,
  "cross": Cross, "star": Star, "ban": Ban, "utensils": UtensilsCrossed,
  "hand-heart": HandHeart, "bell": Bell, "flame": Flame, "circle-check": CircleCheck,
};

// Default icon per category — used when creating custom habits
const CATEGORY_DEFAULT_ICON = {
  prayer: "heart",
  sacramental: "church",
  fasting: "utensils",
  mercy: "hand-heart",
  custom: "circle-check",
};

const CATEGORY_COLORS = {
  prayer: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
  sacramental: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  fasting: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  mercy: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  custom: { bg: "bg-stone-50", text: "text-stone-700", border: "border-stone-200" },
};

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

function FrequencyLabel({ habit }) {
  const period = habit.period || "day";
  const target = habit.target_count || 1;
  const days = habit.target_days || [];
  if (period === "day" && target === 1) return <span>daily</span>;
  if (period === "day" && target > 1) return <span>{target}x / day</span>;
  if (period === "week" && days.length > 0) return <span>{days.map(d => DAY_LABELS[d] || d).join(", ")}</span>;
  if (period === "week" && target > 1) return <span>{target}x / week</span>;
  if (period === "week") return <span>weekly</span>;
  return <span>daily</span>;
}

function HabitCheckItem({ habit, onToggle, isPast, justCompleted, onJournal }) {
  const Icon = ICON_MAP[habit.icon] || ICON_MAP[CATEGORY_DEFAULT_ICON[habit.category]] || CircleCheck;
  const catColors = CATEGORY_COLORS[habit.category] || CATEGORY_COLORS.custom;
  const isMulti = (habit.target_count || 1) > 1;
  const count = habit.count || 0;
  const target = habit.target_count || 1;
  if (habit.due_today === false) return null;

  return (
    <div className="relative group">
      <button
        onClick={() => onToggle(habit.habit_id)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 ${
          habit.completed
            ? "bg-[var(--liturgical-primary)]/5 border-[var(--liturgical-primary)]/30"
            : "bg-white border-[#E5E0D5] hover:border-[var(--liturgical-primary)]/40 hover:shadow-sm"
        } ${justCompleted === habit.habit_id ? "animate-checkoff" : ""}`}
        data-testid={`habit-check-${habit.habit_id}`}
      >
        {isMulti ? (
          <div className={`relative w-6 h-6 flex-shrink-0 transition-transform duration-300 ${justCompleted === habit.habit_id ? "scale-125" : ""}`}>
            <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" stroke="#E5E0D5" strokeWidth="2.5" />
              <circle cx="12" cy="12" r="10" fill="none" stroke={habit.completed ? "var(--liturgical-primary)" : "#9CA3AF"} strokeWidth="2.5" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 10}`} strokeDashoffset={`${2 * Math.PI * 10 * (1 - count / target)}`} className="transition-all duration-500" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold font-['Nunito']" style={{ color: habit.completed ? "var(--liturgical-primary)" : "#6B665E" }}>{count}</span>
          </div>
        ) : habit.completed ? (
          <CheckCircle2 className={`w-6 h-6 flex-shrink-0 transition-transform duration-300 ${justCompleted === habit.habit_id ? "scale-125" : ""}`} style={{ color: "var(--liturgical-primary)" }} />
        ) : (
          <Circle className="w-6 h-6 text-[#D5D0C8] flex-shrink-0 transition-transform duration-200" />
        )}
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${catColors.bg} transition-all duration-300`}>
          <Icon className={`w-4 h-4 ${catColors.text}`} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`font-['Nunito'] text-sm font-medium transition-all duration-300 truncate ${habit.completed ? "text-[#6B665E] line-through" : "text-[#2D2A26]"}`}>{habit.name}</p>
            {habit.challenge_title && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-['Nunito'] font-semibold border border-amber-200 bg-amber-50 text-amber-700 flex-shrink-0" data-testid={`habit-challenge-badge-${habit.habit_id}`}>
                <Trophy className="w-2.5 h-2.5" /> {habit.challenge_title.length > 15 ? habit.challenge_title.slice(0, 15) + '...' : habit.challenge_title}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-[#9B968E] font-['Nunito']"><FrequencyLabel habit={habit} /></span>
            {habit.streak > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-orange-500 font-['Nunito'] font-semibold">
                <Flame className="w-3 h-3" /> {habit.streak}{(habit.period || "day") === "week" ? "w" : "d"}
              </span>
            )}
          </div>
        </div>
        {isMulti && <span className="font-['Nunito'] text-xs text-[#6B665E]">{count}/{target}</span>}
      </button>
      {habit.completed && onJournal && (
        <button
          onClick={(e) => { e.stopPropagation(); onJournal(habit.habit_id); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-[#F0EBE0] transition-all"
          title="Add journal note"
          data-testid={`journal-note-btn-${habit.habit_id}`}
        >
          <FileText className="w-3.5 h-3.5 text-[#9B968E]" />
        </button>
      )}
    </div>
  );
}

function Heatmap({ data, days = 90 }) {
  const cells = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const val = data[key];
    cells.push({ key, val: val !== undefined ? val : -1 });
  }
  return (
    <div className="flex flex-wrap gap-[3px]" data-testid="habit-heatmap">
      {cells.map((c) => (
        <div key={c.key} className="w-3 h-3 rounded-sm" style={{
          backgroundColor: c.val === -1 ? "#F0EBE0" : c.val === 100 ? "var(--liturgical-primary)" : c.val >= 50 ? `color-mix(in srgb, var(--liturgical-primary) ${c.val}%, #F0EBE0)` : c.val > 0 ? `color-mix(in srgb, var(--liturgical-primary) 30%, #F0EBE0)` : "#F0EBE0",
          opacity: c.val === -1 ? 0.3 : 1,
        }} title={c.val >= 0 ? `${c.key}: ${c.val}%` : c.key} />
      ))}
    </div>
  );
}

function MonthlyGrid({ months }) {
  if (!months || months.length === 0) return null;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="monthly-stats-grid">
      {months.map((m) => (
        <div key={`${m.year}-${m.month}`} className="rounded-xl border border-[#E5E0D5] p-2.5 text-center bg-white hover:shadow-sm transition-shadow">
          <p className="font-['Nunito'] text-[10px] text-[#9B968E] mb-0.5">{m.label}</p>
          <p className="font-['Crimson_Text'] text-lg font-bold" style={{
            color: m.compliance >= 80 ? "var(--liturgical-primary)" : m.compliance >= 50 ? "#D97706" : "#9B968E"
          }}>{m.compliance}%</p>
          <p className="font-['Nunito'] text-[9px] text-[#9B968E]">{m.completed}/{m.due}</p>
        </div>
      ))}
    </div>
  );
}

const STREAK_MILESTONES = [7, 14, 30, 60, 100, 200, 365];

function StreakMilestoneCelebration({ streak, habitName, onClose }) {
  if (!streak) return null;
  const messages = {
    7: "First week complete!",
    14: "Two weeks strong!",
    30: "A full month of faithfulness!",
    60: "Two months of dedication!",
    100: "100 days! Incredible perseverance!",
    200: "200 days! A true pillar of faith!",
    365: "A full year! God bless your devotion!",
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-xs text-center">
        <div className="py-4">
          <div className="relative mx-auto w-20 h-20 mb-4">
            <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: "var(--liturgical-primary)" }} />
            <div className="relative w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--liturgical-primary)" }}>
              <Flame className="w-10 h-10 text-white animate-bounce" />
            </div>
          </div>
          <h3 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">{streak}-Day Streak!</h3>
          <p className="font-['Nunito'] text-sm text-[#6B665E] mt-1">{messages[streak] || `${streak} days of faithfulness!`}</p>
          <p className="font-['Nunito'] text-xs text-[#9B968E] mt-2">{habitName}</p>
          <Button onClick={onClose} className="mt-4 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="close-milestone-btn">
            Glory be to God!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateCard({ template, onApply, applying }) {
  return (
    <Card className="border-[#E5E0D5] bg-white hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">{template.name}</h3>
            <p className="font-['Nunito'] text-xs text-[#6B665E] mt-0.5">{template.description}</p>
          </div>
          <Sparkles className="w-4 h-4 text-amber-400 flex-shrink-0 mt-1" />
        </div>
        <div className="flex flex-wrap gap-1 mt-2 mb-3">
          {template.habits.map((h, i) => {
            const freq = h.period === "week" ? (h.target_days?.length ? h.target_days.map(d => DAY_LABELS[d]).join(",") : "weekly") : h.target_count > 1 ? `${h.target_count}x/day` : "";
            return <Badge key={i} variant="outline" className="text-[10px] border-[#E5E0D5] text-[#6B665E]">{h.name}{freq ? ` (${freq})` : ""}</Badge>;
          })}
        </div>
        <Button size="sm" onClick={() => onApply(template.id)} disabled={applying} className="w-full h-8 text-xs bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid={`apply-template-${template.id}`}>
          {applying ? "Adding..." : `Add ${template.habits.length} Habits`}
        </Button>
      </CardContent>
    </Card>
  );
}

function StatsOverview({ stats }) {
  if (!stats) return null;
  const bestStreak = stats.habit_stats?.length > 0 ? Math.max(...stats.habit_stats.map(h => h.streak)) : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="habit-stats">
      <div className="bg-white rounded-xl border border-[#E5E0D5] p-3 text-center">
        <p className="font-['Crimson_Text'] text-2xl font-bold" style={{ color: "var(--liturgical-primary)" }}>{stats.total_completions}</p>
        <p className="font-['Nunito'] text-xs text-[#6B665E]">Total Check-offs</p>
      </div>
      <div className="bg-white rounded-xl border border-[#E5E0D5] p-3 text-center">
        <p className="font-['Crimson_Text'] text-2xl font-bold text-orange-500">{bestStreak}</p>
        <p className="font-['Nunito'] text-xs text-[#6B665E]">Best Active Streak</p>
      </div>
      <div className="bg-white rounded-xl border border-[#E5E0D5] p-3 text-center">
        <p className="font-['Crimson_Text'] text-2xl font-bold text-emerald-600">{stats.full_compliance_days}</p>
        <p className="font-['Nunito'] text-xs text-[#6B665E]">Perfect Days</p>
      </div>
      <div className="bg-white rounded-xl border border-[#E5E0D5] p-3 text-center">
        <p className="font-['Crimson_Text'] text-2xl font-bold text-violet-600">{stats.active_rate}%</p>
        <p className="font-['Nunito'] text-xs text-[#6B665E]">Compliance Rate</p>
      </div>
    </div>
  );
}

export default function RuleOfLifePage() {
  const { season } = useLiturgicalTheme();
  const [checklist, setChecklist] = useState([]);
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(null); // habit to delete
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [newHabit, setNewHabit] = useState({ name: "", category: "prayer", icon: "heart", period: "day", target_count: 1, target_days: [] });
  const [tab, setTab] = useState("today");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [liturgicalSuggestion, setLiturgicalSuggestion] = useState(null);
  const [justCompleted, setJustCompleted] = useState(null);
  const [milestoneData, setMilestoneData] = useState(null);
  const [statsDays, setStatsDays] = useState(90);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [reminderSettings, setReminderSettings] = useState({ enabled: false, time: "08:00" });
  const [reminderSaving, setReminderSaving] = useState(false);
  const [editHabit, setEditHabit] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", category: "prayer", icon: "heart", period: "day", target_count: 1, target_days: [] });
  const [journalHabitId, setJournalHabitId] = useState(null);
  const [journalNote, setJournalNote] = useState("");
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalDays, setJournalDays] = useState(30);
  const [exporting, setExporting] = useState(false);

  const isToday = selectedDate.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);
  const dateStr = selectedDate.toISOString().slice(0, 10);

  const loadChecklist = useCallback(async (date) => {
    try {
      const res = await getHabitsToday(date);
      setChecklist(res.data.checklist);
      setCompleted(res.data.completed);
      setTotal(res.data.total);
    } catch (err) {
      console.error("Failed to load habits:", err);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await getHabitStats(statsDays);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, [statsDays]);

  const loadMonthlyStats = useCallback(async () => {
    try {
      const res = await getMonthlyStats(12);
      setMonthlyStats(res.data.months);
    } catch {}
  }, []);

  const loadReminderSettings = useCallback(async () => {
    try {
      const res = await getHabitReminderSettings();
      setReminderSettings(res.data);
    } catch {}
  }, []);

  const loadJournalEntries = useCallback(async () => {
    try {
      const res = await getJournalEntries(journalDays);
      setJournalEntries(res.data.entries || []);
    } catch {}
  }, [journalDays]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await getHabitTemplates();
      setTemplates(res.data.templates);
    } catch {}
  }, []);

  const loadLiturgicalSuggestion = useCallback(async () => {
    try {
      const res = await getLiturgicalSuggestions();
      setLiturgicalSuggestion(res.data.suggestion);
    } catch {}
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadChecklist(dateStr), loadStats(), loadTemplates(), loadLiturgicalSuggestion(), loadReminderSettings()]);
      setLoading(false);
    };
    load();
  }, [loadChecklist, loadStats, loadTemplates, loadLiturgicalSuggestion, loadReminderSettings, dateStr]);

  useEffect(() => {
    if (tab === "stats") {
      loadStats();
      loadMonthlyStats();
    }
    if (tab === "journal") {
      loadJournalEntries();
    }
  }, [tab, loadStats, loadMonthlyStats, loadJournalEntries]);

  // Reload checklist when date changes
  useEffect(() => {
    if (!loading) loadChecklist(dateStr);
  }, [dateStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (habitId) => {
    const habit = checklist.find(h => h.habit_id === habitId);
    const isMulti = (habit?.target_count || 1) > 1;
    const prevStreak = habit?.streak || 0;

    setChecklist(prev => prev.map(h => {
      if (h.habit_id !== habitId) return h;
      if (isMulti) {
        const newCount = h.completed ? 0 : (h.count || 0) + 1;
        return { ...h, count: newCount, completed: newCount >= (h.target_count || 1) };
      }
      return { ...h, completed: !h.completed };
    }));

    // Trigger check-off animation
    setJustCompleted(habitId);
    setTimeout(() => setJustCompleted(null), 600);

    try {
      const res = await logHabit(habitId, { date: dateStr });
      const newStreak = res.data.streak || 0;

      setChecklist(prev => prev.map(h =>
        h.habit_id === habitId ? { ...h, streak: newStreak, best_streak: res.data.best_streak, completed: res.data.completed, count: res.data.count } : h
      ));
      setChecklist(prev => {
        const due = prev.filter(h => h.due_today !== false);
        setCompleted(due.filter(h => h.completed).length);
        setTotal(due.length);
        return prev;
      });

      // Check for streak milestones
      if (res.data.completed && newStreak > prevStreak) {
        const milestone = STREAK_MILESTONES.find(m => newStreak >= m && prevStreak < m);
        if (milestone) {
          setTimeout(() => setMilestoneData({ streak: milestone, habitName: habit?.name }), 400);
        }
      }
    } catch {
      loadChecklist(dateStr);
      toast.error("Failed to update habit");
    }
  };

  const handleApplyTemplate = async (templateId) => {
    setApplying(true);
    try {
      const res = await applyHabitTemplate(templateId);
      toast.success(`Added ${res.data.added} habits from "${res.data.template}"`);
      await loadChecklist(dateStr);
      setShowTemplates(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply template");
    }
    setApplying(false);
  };

  const handleCreateHabit = async () => {
    if (!newHabit.name.trim()) return;
    try {
      await createHabit(newHabit);
      toast.success(`"${newHabit.name}" added`);
      setNewHabit({ name: "", category: "prayer", icon: "heart", period: "day", target_count: 1, target_days: [] });
      setShowAddDialog(false);
      await loadChecklist(dateStr);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create habit");
    }
  };

  const confirmDeleteHabit = async () => {
    if (!showDeleteDialog) return;
    try {
      await deleteHabit(showDeleteDialog.habit_id);
      toast.success(`"${showDeleteDialog.name}" removed`);
      setShowDeleteDialog(null);
      await loadChecklist(dateStr);
    } catch {
      toast.error("Failed to delete habit");
    }
  };

  const confirmResetProgress = async () => {
    try {
      const res = await resetHabitProgress();
      toast.success(`Progress reset — ${res.data.logs_deleted} log${res.data.logs_deleted !== 1 ? 's' : ''} cleared`);
      setShowResetDialog(false);
      await Promise.all([loadChecklist(dateStr), loadStats()]);
    } catch {
      toast.error("Failed to reset progress");
    }
  };

  const handleDismissSuggestion = async () => {
    if (!liturgicalSuggestion) return;
    try {
      await dismissLiturgicalSuggestion(liturgicalSuggestion.suggestion_id);
      setLiturgicalSuggestion(null);
    } catch {}
  };

  const handleAddSuggestedHabits = async () => {
    if (!liturgicalSuggestion) return;
    try {
      const res = await addSuggestedHabits(liturgicalSuggestion.suggestion_id);
      toast.success(`Added ${res.data.added} ${res.data.season} habits!`);
      setLiturgicalSuggestion(null);
      await loadChecklist(dateStr);
    } catch (err) {
      toast.error("Failed to add habits");
    }
  };

  const handleSaveReminder = async (enabled, time) => {
    setReminderSaving(true);
    try {
      await updateHabitReminderSettings({ enabled, time });
      setReminderSettings({ enabled, time });
      toast.success(enabled ? `Reminder set for ${time}` : "Reminder disabled");
    } catch {
      toast.error("Failed to save reminder settings");
    }
    setReminderSaving(false);
  };

  const openEditDialog = (habit) => {
    setEditHabit(habit);
    setEditForm({
      name: habit.name,
      category: habit.category || "custom",
      icon: habit.icon || "circle-check",
      period: habit.period || "day",
      target_count: habit.target_count || 1,
      target_days: habit.target_days || [],
    });
  };

  const handleSaveEdit = async () => {
    if (!editHabit || !editForm.name.trim()) return;
    try {
      await updateHabit(editHabit.habit_id, editForm);
      toast.success(`"${editForm.name}" updated`);
      setEditHabit(null);
      await loadChecklist(dateStr);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update habit");
    }
  };

  const handleMoveHabit = async (index, direction) => {
    const newList = [...checklist];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newList.length) return;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    setChecklist(newList);
    const reorderData = newList.map((h, i) => ({ habit_id: h.habit_id, order: i }));
    try {
      await reorderHabits(reorderData);
    } catch {
      toast.error("Failed to reorder");
      await loadChecklist(dateStr);
    }
  };

  const handleAddJournalNote = async (habitId) => {
    if (!journalNote.trim()) { setJournalHabitId(null); return; }
    try {
      await updateLogNotes(habitId, journalNote, dateStr);
      toast.success("Journal note saved");
      setJournalHabitId(null);
      setJournalNote("");
    } catch {
      toast.error("Check off the habit first, then add a note");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportHabitsCsv(statsDays);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rule_of_life_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded!");
    } catch {
      toast.error("Failed to export data");
    }
    setExporting(false);
  };

  const navigateDate = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    if (d <= new Date()) setSelectedDate(d);
  };

  const dueHabits = checklist.filter(h => h.due_today !== false);
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--liturgical-primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-12" data-testid="rule-of-life-page">
      <div className="mb-6">
        <h1 className="font-['Crimson_Text'] text-3xl md:text-4xl font-bold text-[#2D2A26]">My Rule of Life</h1>
        <p className="font-['Nunito'] text-sm text-[#6B665E] mt-1">Track your daily spiritual disciplines and grow in holiness.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#F0EBE0] rounded-lg p-1 overflow-x-auto" data-testid="habit-tabs">
        {[
          { id: "today", label: "Today", icon: CalendarIcon },
          { id: "stats", label: "Progress", icon: BarChart3 },
          { id: "journal", label: "Journal", icon: BookMarked },
          { id: "challenges", label: "Challenges", icon: Trophy },
          { id: "partners", label: "Partners", icon: Users },
          { id: "manage", label: "Manage", icon: Settings },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs sm:text-sm font-['Nunito'] font-medium transition-all whitespace-nowrap ${tab === t.id ? "bg-white text-[#2D2A26] shadow-sm" : "text-[#6B665E] hover:text-[#2D2A26]"}`} data-testid={`habit-tab-${t.id}`}>
            <t.icon className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Liturgical Season Suggestion Banner */}
      {tab === "today" && liturgicalSuggestion && (
        <div className="mb-4 rounded-xl border border-[var(--liturgical-primary)]/20 bg-[var(--liturgical-primary)]/5 p-4 relative" data-testid="liturgical-suggestion-banner">
          <button onClick={handleDismissSuggestion} className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/5 transition-colors" data-testid="dismiss-suggestion-btn">
            <X className="w-4 h-4 text-[#6B665E]" />
          </button>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--liturgical-primary)", opacity: 0.15 }}>
              <Leaf className="w-5 h-5" style={{ color: "var(--liturgical-primary)" }} />
            </div>
            <div className="flex-1 min-w-0 pr-4">
              <p className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">{liturgicalSuggestion.title}</p>
              <p className="font-['Nunito'] text-xs text-[#6B665E] mt-0.5">{liturgicalSuggestion.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {liturgicalSuggestion.habits?.map((h, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-['Nunito'] font-medium border border-[var(--liturgical-primary)]/20" style={{ color: "var(--liturgical-primary)" }}>
                    {h.name}
                  </span>
                ))}
              </div>
              <Button size="sm" onClick={handleAddSuggestedHabits} className="mt-3 h-7 text-xs bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="add-suggested-habits-btn">
                <Plus className="w-3 h-3 mr-1" /> Add {liturgicalSuggestion.habits?.length} Habits
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* TODAY TAB */}
      {tab === "today" && (
        <div className="space-y-4">
          {/* Date Navigator */}
          <div className="flex items-center justify-between">
            <button onClick={() => navigateDate(-1)} className="p-1.5 rounded-lg hover:bg-[#F0EBE0] transition-colors" data-testid="date-prev">
              <ChevronLeft className="w-5 h-5 text-[#6B665E]" />
            </button>
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[#F0EBE0] transition-colors"
              data-testid="date-picker-toggle"
            >
              <CalendarIcon className="w-4 h-4 text-[#6B665E]" />
              <span className="font-['Nunito'] text-sm font-medium text-[#2D2A26]">
                {isToday ? "Today" : selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
              {!isToday && (
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedDate(new Date()); }}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--liturgical-primary)]/10 font-['Nunito'] font-medium"
                  style={{ color: "var(--liturgical-primary)" }}
                >
                  Back to today
                </button>
              )}
            </button>
            <button onClick={() => navigateDate(1)} disabled={isToday} className={`p-1.5 rounded-lg hover:bg-[#F0EBE0] transition-colors ${isToday ? "opacity-30 cursor-not-allowed" : ""}`} data-testid="date-next">
              <ChevronRight className="w-5 h-5 text-[#6B665E]" />
            </button>
          </div>

          {/* Calendar popup */}
          {showCalendar && (
            <Card className="border-[#E5E0D5] bg-white">
              <CardContent className="p-2 flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => { if (d && d <= new Date()) { setSelectedDate(d); setShowCalendar(false); } }}
                  disabled={(d) => d > new Date()}
                  className="font-['Nunito']"
                />
              </CardContent>
            </Card>
          )}

          {/* Progress ring */}
          <Card className="border-[#E5E0D5] bg-white overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center gap-5">
                <div className="relative w-20 h-20 flex-shrink-0">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="35" fill="none" stroke="#F0EBE0" strokeWidth="6" />
                    <circle cx="40" cy="40" r="35" fill="none" stroke="var(--liturgical-primary)" strokeWidth="6" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 35}`} strokeDashoffset={`${2 * Math.PI * 35 * (1 - progressPct / 100)}`} style={{ transition: "stroke-dashoffset 0.5s ease" }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-['Crimson_Text'] text-lg font-bold text-[#2D2A26]">{progressPct}%</span>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">
                    {completed === total && total > 0 ? "All done! God bless you." : `${completed} of ${total} due ${isToday ? "today" : "this day"}`}
                  </p>
                  <p className="font-['Nunito'] text-sm text-[#6B665E]">
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </p>
                  {checklist.length > dueHabits.length && (
                    <p className="font-['Nunito'] text-xs text-[#9B968E] mt-0.5">
                      {checklist.length - dueHabits.length} habit{checklist.length - dueHabits.length !== 1 ? 's' : ''} not due
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Checklist */}
          {dueHabits.length === 0 && checklist.length === 0 ? (
            <Card className="border-[#E5E0D5] bg-white">
              <CardContent className="p-8 text-center">
                <Cross className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--liturgical-primary)", opacity: 0.5 }} />
                <p className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-1">Start Your Rule of Life</p>
                <p className="font-['Nunito'] text-sm text-[#6B665E] mb-4">Choose a template or create custom habits to begin tracking.</p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => setShowTemplates(true)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="browse-templates-btn">
                    <Sparkles className="w-4 h-4 mr-1.5" /> Browse Templates
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddDialog(true)} className="border-[#E5E0D5] font-['Nunito']" data-testid="create-custom-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Custom Habit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : dueHabits.length === 0 ? (
            <Card className="border-[#E5E0D5] bg-white">
              <CardContent className="p-6 text-center">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--liturgical-primary)" }} />
                <p className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">No habits due {isToday ? "today" : "this day"}</p>
                <p className="font-['Nunito'] text-sm text-[#6B665E]">Your weekly habits are scheduled for other days.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="habit-checklist">
              {dueHabits.map((habit) => (
                <div key={habit.habit_id}>
                  <HabitCheckItem habit={habit} onToggle={handleToggle} isPast={!isToday} justCompleted={justCompleted} onJournal={(id) => { setJournalHabitId(id); setJournalNote(""); }} />
                  {journalHabitId === habit.habit_id && (
                    <div className="ml-9 mt-1 mb-2 flex gap-2 items-start animate-in slide-in-from-top-2 duration-200" data-testid={`journal-inline-${habit.habit_id}`}>
                      <Textarea
                        value={journalNote}
                        onChange={(e) => setJournalNote(e.target.value)}
                        placeholder="Reflect on this habit..."
                        className="flex-1 min-h-[60px] text-sm border-[#E5E0D5] font-['Nunito'] resize-none"
                        data-testid="journal-note-input"
                        autoFocus
                      />
                      <div className="flex flex-col gap-1">
                        <Button size="sm" onClick={() => handleAddJournalNote(habit.habit_id)} className="h-7 text-xs bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="save-journal-note-btn">Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setJournalHabitId(null)} className="h-7 text-xs text-[#6B665E] font-['Nunito']">Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isToday && (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setShowTemplates(true)} className="border-[#E5E0D5] text-[#6B665E] font-['Nunito'] text-xs h-8">
                    <Sparkles className="w-3 h-3 mr-1" /> Templates
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowAddDialog(true)} className="border-[#E5E0D5] text-[#6B665E] font-['Nunito'] text-xs h-8">
                    <Plus className="w-3 h-3 mr-1" /> Add Habit
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* STATS TAB */}
      {tab === "stats" && stats && (
        <div className="space-y-4">
          {/* Time Range Toggle */}
          <div className="flex items-center justify-between">
            <h2 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">Progress</h2>
            <div className="flex gap-1 bg-[#F0EBE0] rounded-lg p-0.5" data-testid="stats-range-toggle">
              {[
                { value: 30, label: "30d" },
                { value: 90, label: "90d" },
                { value: 365, label: "1y" },
              ].map(r => (
                <button
                  key={r.value}
                  onClick={() => setStatsDays(r.value)}
                  className={`px-3 py-1 rounded-md text-xs font-['Nunito'] font-medium transition-all ${statsDays === r.value ? "bg-white text-[#2D2A26] shadow-sm" : "text-[#6B665E] hover:text-[#2D2A26]"}`}
                  data-testid={`stats-range-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <StatsOverview stats={stats} />

          {/* Compliance Heatmap */}
          <Card className="border-[#E5E0D5] bg-white">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">{statsDays}-Day Compliance</CardTitle>
                <span className="font-['Nunito'] text-xs text-[#9B968E]">
                  {new Date(Date.now() - (statsDays - 1) * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <Heatmap data={stats.compliance_heatmap || {}} days={statsDays} />
              <div className="flex items-center justify-between mt-2">
                <span className="font-['Nunito'] text-[10px] text-[#9B968E]">
                  {new Date(Date.now() - (statsDays - 1) * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <div className="flex items-center gap-1">
                  <span className="font-['Nunito'] text-[10px] text-[#6B665E]">0%</span>
                  {[0, 25, 50, 75, 100].map((v, i) => (
                    <div key={i} className="w-3 h-3 rounded-sm" style={{
                      backgroundColor: v === 0 ? "#F0EBE0" : v === 100 ? "var(--liturgical-primary)" : `color-mix(in srgb, var(--liturgical-primary) ${v}%, #F0EBE0)`,
                    }} />
                  ))}
                  <span className="font-['Nunito'] text-[10px] text-[#6B665E]">100%</span>
                </div>
                <span className="font-['Nunito'] text-[10px] text-[#9B968E]">
                  {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Summary */}
          {monthlyStats && monthlyStats.length > 0 && (
            <Card className="border-[#E5E0D5] bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#6B665E]" /> Monthly Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MonthlyGrid months={monthlyStats} />
              </CardContent>
            </Card>
          )}

          {/* Streaks */}
          <Card className="border-[#E5E0D5] bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" /> Streaks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.habit_stats?.sort((a, b) => b.streak - a.streak).map((h) => {
                const Icon = ICON_MAP[h.icon] || ICON_MAP[CATEGORY_DEFAULT_ICON[h.category]] || CircleCheck;
                const catColors = CATEGORY_COLORS[h.category] || CATEGORY_COLORS.custom;
                const unit = (h.period || "day") === "week" ? "w" : "d";
                return (
                  <div key={h.habit_id} className="flex items-center gap-3 py-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${catColors.bg}`}>
                      <Icon className={`w-4 h-4 ${catColors.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26] truncate">{h.name}</p>
                      <p className="font-['Nunito'] text-xs text-[#6B665E]">
                        {h.completions} total{h.target_count > 1 && ` (${h.target_count}x/${h.period || "day"})`}{h.target_days?.length > 0 && ` (${h.target_days.map(d => DAY_LABELS[d]).join(", ")})`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-['Nunito'] text-sm font-bold text-orange-500 flex items-center gap-1"><Flame className="w-3.5 h-3.5" /> {h.streak}{unit}</p>
                      <p className="font-['Nunito'] text-[10px] text-[#6B665E]">best: {h.best_streak}{unit}</p>
                    </div>
                  </div>
                );
              })}
              {(!stats.habit_stats || stats.habit_stats.length === 0) && (
                <p className="text-center font-['Nunito'] text-sm text-[#6B665E] py-4">No habits tracked yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Reset Progress */}
          {stats.total_completions > 0 && (
            <div className="flex justify-center gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
                className="border-[#E5E0D5] text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] text-xs h-8"
                data-testid="export-csv-btn"
              >
                <Download className="w-3 h-3 mr-1.5" />
                {exporting ? "Exporting..." : "Export CSV"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResetDialog(true)}
                className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 font-['Nunito'] text-xs h-8"
                data-testid="reset-progress-btn"
              >
                <Trash2 className="w-3 h-3 mr-1.5" />
                Reset All Progress
              </Button>
            </div>
          )}
        </div>
      )}

      {/* MANAGE TAB */}
      {tab === "manage" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-['Nunito'] text-sm text-[#6B665E]">{checklist.length} habit{checklist.length !== 1 ? 's' : ''}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowTemplates(true)} className="border-[#E5E0D5] font-['Nunito'] text-xs h-8">
                <Sparkles className="w-3 h-3 mr-1" /> Templates
              </Button>
              <Button size="sm" onClick={() => setShowAddDialog(true)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-xs h-8" data-testid="manage-add-habit-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Habit
              </Button>
            </div>
          </div>
          {checklist.length === 0 ? (
            <Card className="border-[#E5E0D5] bg-white"><CardContent className="p-8 text-center"><p className="font-['Nunito'] text-sm text-[#6B665E]">No habits yet. Add some to get started!</p></CardContent></Card>
          ) : (
            <div className="space-y-2">
              {checklist.map((habit, idx) => {
                const Icon = ICON_MAP[habit.icon] || ICON_MAP[CATEGORY_DEFAULT_ICON[habit.category]] || CircleCheck;
                const catColors = CATEGORY_COLORS[habit.category] || CATEGORY_COLORS.custom;
                return (
                  <div key={habit.habit_id} className="flex items-center gap-2 bg-white border border-[#E5E0D5] rounded-xl px-3 py-3" data-testid={`manage-habit-${habit.habit_id}`}>
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => handleMoveHabit(idx, -1)} disabled={idx === 0} className={`p-0.5 rounded hover:bg-[#F0EBE0] transition-colors ${idx === 0 ? "opacity-20 cursor-not-allowed" : ""}`} data-testid={`move-up-${habit.habit_id}`}>
                        <ArrowUp className="w-3.5 h-3.5 text-[#6B665E]" />
                      </button>
                      <button onClick={() => handleMoveHabit(idx, 1)} disabled={idx === checklist.length - 1} className={`p-0.5 rounded hover:bg-[#F0EBE0] transition-colors ${idx === checklist.length - 1 ? "opacity-20 cursor-not-allowed" : ""}`} data-testid={`move-down-${habit.habit_id}`}>
                        <ArrowDown className="w-3.5 h-3.5 text-[#6B665E]" />
                      </button>
                    </div>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${catColors.bg}`}>
                      <Icon className={`w-4 h-4 ${catColors.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26] truncate">{habit.name}</p>
                        {habit.challenge_title && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-['Nunito'] font-semibold border border-amber-200 bg-amber-50 text-amber-700 flex-shrink-0">
                            <Trophy className="w-2.5 h-2.5" /> {habit.challenge_title.length > 12 ? habit.challenge_title.slice(0, 12) + '...' : habit.challenge_title}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${catColors.border} ${catColors.text}`}>{habit.category}</Badge>
                        <span className="text-[10px] text-[#6B665E] font-['Nunito']"><FrequencyLabel habit={habit} /></span>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B665E] hover:text-[#2D2A26] hover:bg-[#F0EBE0]" onClick={() => openEditDialog(habit)} data-testid={`edit-habit-${habit.habit_id}`}>
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => setShowDeleteDialog(habit)} data-testid={`delete-habit-${habit.habit_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Daily Reminder Settings */}
          <Card className="border-[#E5E0D5] bg-white" data-testid="reminder-settings-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50">
                    <Bell className="w-4.5 h-4.5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26]">Daily Reminder</p>
                    <p className="font-['Nunito'] text-xs text-[#6B665E]">
                      {reminderSettings.enabled ? `Notify at ${reminderSettings.time} if habits are incomplete` : "Get a nudge to complete your habits"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleSaveReminder(!reminderSettings.enabled, reminderSettings.time)}
                  disabled={reminderSaving}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${reminderSettings.enabled ? "" : "bg-[#D5D0C8]"}`}
                  style={reminderSettings.enabled ? { backgroundColor: "var(--liturgical-primary)" } : {}}
                  data-testid="reminder-toggle"
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${reminderSettings.enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
              </div>
              {reminderSettings.enabled && (
                <div className="mt-3 flex items-center gap-2">
                  <label className="font-['Nunito'] text-xs text-[#6B665E]">Reminder time:</label>
                  <select
                    value={reminderSettings.time}
                    onChange={(e) => handleSaveReminder(true, e.target.value)}
                    className="font-['Nunito'] text-sm border border-[#E5E0D5] rounded-md px-2 py-1 bg-white text-[#2D2A26]"
                    data-testid="reminder-time-select"
                  >
                    {["06:00", "07:00", "08:00", "09:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00", "21:00"].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* JOURNAL TAB */}
      {tab === "journal" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] flex items-center gap-2">
              <BookMarked className="w-5 h-5 text-[#6B665E]" /> Spiritual Journal
            </h2>
            <div className="flex gap-1 bg-[#F0EBE0] rounded-lg p-0.5" data-testid="journal-range-toggle">
              {[{ value: 7, label: "7d" }, { value: 30, label: "30d" }, { value: 90, label: "90d" }].map(r => (
                <button key={r.value} onClick={() => setJournalDays(r.value)}
                  className={`px-3 py-1 rounded-md text-xs font-['Nunito'] font-medium transition-all ${journalDays === r.value ? "bg-white text-[#2D2A26] shadow-sm" : "text-[#6B665E] hover:text-[#2D2A26]"}`}
                  data-testid={`journal-range-${r.value}`}
                >{r.label}</button>
              ))}
            </div>
          </div>

          {journalEntries.length === 0 ? (
            <Card className="border-[#E5E0D5] bg-white">
              <CardContent className="p-8 text-center">
                <BookMarked className="w-10 h-10 mx-auto mb-3 text-[#D5D0C8]" />
                <p className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-1">No Journal Entries Yet</p>
                <p className="font-['Nunito'] text-sm text-[#6B665E]">After completing a habit, hover and click the note icon to reflect on your practice.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="journal-entries-list">
              {journalEntries.map((entry, i) => {
                const EntryIcon = ICON_MAP[entry.habit_icon] || CircleCheck;
                const catColors = CATEGORY_COLORS[entry.habit_category] || CATEGORY_COLORS.custom;
                const prevDate = i > 0 ? journalEntries[i - 1].date : null;
                const showDateHeader = entry.date !== prevDate;
                return (
                  <div key={entry.log_id || i}>
                    {showDateHeader && (
                      <p className="font-['Nunito'] text-xs text-[#9B968E] font-semibold uppercase tracking-wider mt-2 mb-1">
                        {new Date(entry.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                      </p>
                    )}
                    <div className="bg-white border border-[#E5E0D5] rounded-xl p-4" data-testid={`journal-entry-${entry.log_id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${catColors.bg}`}>
                          <EntryIcon className={`w-4 h-4 ${catColors.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26]">{entry.habit_name}</p>
                          <p className="font-['Nunito'] text-sm text-[#4A4540] mt-1 whitespace-pre-wrap">{entry.notes}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "challenges" && <ChallengesTab />}

      {/* PARTNERS TAB */}
      {tab === "partners" && <AccountabilityTab />}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteDialog} onOpenChange={(open) => !open && setShowDeleteDialog(null)}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Remove Habit
            </DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Are you sure you want to remove <strong>"{showDeleteDialog?.name}"</strong> from your Rule of Life? This will also delete all of its completion history and streaks. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteDialog(null)} className="border-[#E5E0D5] font-['Nunito']">
              Cancel
            </Button>
            <Button onClick={confirmDeleteHabit} className="bg-red-600 hover:bg-red-700 text-white font-['Nunito']" data-testid="confirm-delete-habit">
              Remove Habit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Progress Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Reset All Progress
            </DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Are you sure you want to reset all progress? This will delete all completion history, streaks, and stats for every habit. Your habits will remain, but all check-offs will be cleared. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowResetDialog(false)} className="border-[#E5E0D5] font-['Nunito']">Cancel</Button>
            <Button onClick={confirmResetProgress} className="bg-red-600 hover:bg-red-700 text-white font-['Nunito']" data-testid="confirm-reset-progress">Reset Progress</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" /> Habit Templates
            </DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Choose a template to quickly add a set of spiritual habits.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="space-y-3 py-2">
              {templates.map((t) => <TemplateCard key={t.id} template={t} onApply={handleApplyTemplate} applying={applying} />)}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Add Habit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">Add Custom Habit</DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Create a new spiritual habit to track.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Name</label>
              <Input value={newHabit.name} onChange={(e) => setNewHabit(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Divine Mercy Chaplet" className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="new-habit-name" />
            </div>
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Category</label>
              <Select value={newHabit.category} onValueChange={(v) => setNewHabit(prev => ({ ...prev, category: v, icon: CATEGORY_DEFAULT_ICON[v] || "circle-check" }))}>
                <SelectTrigger className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="new-habit-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prayer">Prayer</SelectItem>
                  <SelectItem value="sacramental">Sacramental</SelectItem>
                  <SelectItem value="fasting">Fasting & Sacrifice</SelectItem>
                  <SelectItem value="mercy">Works of Mercy</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Frequency</label>
              <Select value={newHabit.period} onValueChange={(v) => setNewHabit(prev => ({ ...prev, period: v, target_days: v === "day" ? [] : prev.target_days }))}>
                <SelectTrigger className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="new-habit-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Times per {newHabit.period === "day" ? "day" : "week"}</label>
              <Select value={String(newHabit.target_count)} onValueChange={(v) => setNewHabit(prev => ({ ...prev, target_count: parseInt(v) }))}>
                <SelectTrigger className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="new-habit-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map(n => <SelectItem key={n} value={String(n)}>{n}x</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newHabit.period === "week" && (
              <div>
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">On specific days (optional)</label>
                <div className="flex gap-1 mt-1.5">
                  {DAYS.map(d => (
                    <button key={d} onClick={() => setNewHabit(prev => ({ ...prev, target_days: prev.target_days.includes(d) ? prev.target_days.filter(x => x !== d) : [...prev.target_days, d] }))}
                      className={`flex-1 py-1.5 rounded-md text-xs font-['Nunito'] font-medium transition-colors ${newHabit.target_days.includes(d) ? "text-white" : "bg-[#F0EBE0] text-[#6B665E] hover:bg-[#E5E0D5]"}`}
                      style={newHabit.target_days.includes(d) ? { backgroundColor: "var(--liturgical-primary)" } : {}}
                      data-testid={`day-toggle-${d}`}
                    >{DAY_LABELS[d]}</button>
                  ))}
                </div>
                {newHabit.target_days.length === 0 && <p className="font-['Nunito'] text-[10px] text-[#9B968E] mt-1">No days selected = any day of the week</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-[#E5E0D5] font-['Nunito']">Cancel</Button>
            <Button onClick={handleCreateHabit} disabled={!newHabit.name.trim()} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="save-new-habit-btn">Add Habit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Habit Dialog */}
      <Dialog open={!!editHabit} onOpenChange={(open) => !open && setEditHabit(null)}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">Edit Habit</DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Update your habit details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Name</label>
              <Input value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="edit-habit-name" />
            </div>
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Category</label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm(prev => ({ ...prev, category: v, icon: CATEGORY_DEFAULT_ICON[v] || "circle-check" }))}>
                <SelectTrigger className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="edit-habit-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prayer">Prayer</SelectItem>
                  <SelectItem value="sacramental">Sacramental</SelectItem>
                  <SelectItem value="fasting">Fasting & Sacrifice</SelectItem>
                  <SelectItem value="mercy">Works of Mercy</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Frequency</label>
              <Select value={editForm.period} onValueChange={(v) => setEditForm(prev => ({ ...prev, period: v, target_days: v === "day" ? [] : prev.target_days }))}>
                <SelectTrigger className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="edit-habit-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">Times per {editForm.period === "day" ? "day" : "week"}</label>
              <Select value={String(editForm.target_count)} onValueChange={(v) => setEditForm(prev => ({ ...prev, target_count: parseInt(v) }))}>
                <SelectTrigger className="mt-1 border-[#E5E0D5] font-['Nunito']" data-testid="edit-habit-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map(n => <SelectItem key={n} value={String(n)}>{n}x</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editForm.period === "week" && (
              <div>
                <label className="font-['Nunito'] text-sm text-[#2D2A26] font-medium">On specific days (optional)</label>
                <div className="flex gap-1 mt-1.5">
                  {DAYS.map(d => (
                    <button key={d} onClick={() => setEditForm(prev => ({ ...prev, target_days: prev.target_days.includes(d) ? prev.target_days.filter(x => x !== d) : [...prev.target_days, d] }))}
                      className={`flex-1 py-1.5 rounded-md text-xs font-['Nunito'] font-medium transition-colors ${editForm.target_days.includes(d) ? "text-white" : "bg-[#F0EBE0] text-[#6B665E] hover:bg-[#E5E0D5]"}`}
                      style={editForm.target_days.includes(d) ? { backgroundColor: "var(--liturgical-primary)" } : {}}
                      data-testid={`edit-day-toggle-${d}`}
                    >{DAY_LABELS[d]}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditHabit(null)} className="border-[#E5E0D5] font-['Nunito']">Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.name.trim()} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="save-edit-habit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Streak Milestone Celebration */}
      {milestoneData && (
        <StreakMilestoneCelebration
          streak={milestoneData.streak}
          habitName={milestoneData.habitName}
          onClose={() => setMilestoneData(null)}
        />
      )}

      {/* Check-off Animation CSS */}
      <style>{`
        @keyframes checkoff {
          0% { transform: scale(1); }
          30% { transform: scale(1.03); background-color: rgba(var(--liturgical-primary-rgb, 76, 104, 68), 0.08); }
          100% { transform: scale(1); }
        }
        .animate-checkoff {
          animation: checkoff 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
